/**
 * useMrnLabel
 *
 * Single source of truth for the tenant-configurable MRN term.
 *
 * Some regions call the medical record number something else — the Bielefeld
 * (DE) site calls it "EKB". That term is a *label*: it changes what the field
 * is called, never the stored value. ADT-fed and manually entered MRNs are
 * kept verbatim, because the hospital feed is the source of truth for the
 * identifier itself.
 *
 * Rather than swapping the term at each call site, the term is handed to
 * i18next as the `{{mrnLabel}}` interpolation variable, and every MRN string
 * in the locale bundle references it:
 *
 *   "patient.profile.mrnLabel":         "{{mrnLabel}}: {{mrn}}"
 *   "common.searchPatientByNameOrMrn":  "Search patients by name or {{mrnLabel}}..."
 *   "apiErrors.duplicateMrn":           "This {{mrnLabel}} is already in use. ..."
 *
 * Two consequences worth knowing:
 *
 *  1. It reaches strings where the term is embedded mid-sentence — the
 *     majority of them — which a per-call-site label swap cannot do.
 *  2. No component changes. Every existing `t()` / `$t()` call keeps working
 *     unmodified, so there is no risk of missing one.
 *
 * Why the term can be changed at runtime: i18next reads
 * `interpolation.defaultVariables` fresh inside every `interpolate()` call and
 * deliberately does NOT destructure it into the Interpolator at init time (see
 * Interpolator#init vs Interpolator#interpolate). The Interpolator also holds
 * `i18next.options` by reference, so assigning through `i18next.options` is
 * picked up immediately — no re-init, no bundle reload.
 *
 * `i18next-vue` only re-renders on `languageChanged`, `loaded`, or the
 * ResourceStore's `added` event, so we emit `added` to refresh bindings that
 * are already mounted.
 *
 * The term is published in two stages, because the authoritative source is only
 * reachable once the user is authenticated:
 *
 *  1. *Provisional* — the login page publishes `mrn_prefix` from the pre-auth
 *     tenant-config response (`useOidc`). That endpoint resolves the tenant from
 *     the hostname, which is not a reliable identity for the hospital, so this
 *     value is live-only and never cached.
 *  2. *Authoritative* — after login, `useTenantSupportEmail` reads the same
 *     attribute scoped to the authenticated `hospital_id` and republishes it.
 *     Only this stage is persisted. See the {@link MRN_LABEL_CACHE_KEY} note.
 */
import { config } from '@/core/config';
import { i18next } from '@/core/plugins/i18n';
import { MRN_LABEL_DEFAULT_KEY, MRN_LABEL_FALLBACK, MRN_LABEL_VAR } from '@/shared/utils/mrn';

const _mrnLabel = ref<string>(MRN_LABEL_FALLBACK);

/**
 * localStorage key holding the last known term, scoped to the hospital.
 *
 * Mirrors the locale bundle's L2 cache in `core/plugins/i18n.ts`: the term is
 * needed synchronously at boot, but it is only fetched over the network after
 * login, so without a persisted copy every reload of an authenticated session
 * would show "MRN" until that fetch resolved.
 *
 * Scoped by `hospital_id`, NOT by hostname. The pre-auth tenant-config endpoint
 * resolves the tenant with a DynamoDB *scan* on `tenant_hostname` and returns
 * the first match, and nothing enforces that attribute to be unique — so on a
 * shared dev/QA domain it can return a different tenant record than the
 * hospital the user actually authenticates into. Keying on the authenticated
 * `hospital_id` is the rule the settings read path already follows (see
 * `fetchContactDetails` in `modules/settings/services/settings.api.ts`, which
 * documents the same hazard); a hostname-keyed entry would persist one tenant's
 * term and replay it into another tenant's session on the next boot.
 *
 * Only authoritative, hospital-scoped terms are written here — see
 * {@link persist}. Entries written by older builds carried a `host` field
 * instead; they fail the `hospitalId` comparison, are ignored, and are
 * overwritten by the next publish.
 */
const MRN_LABEL_CACHE_KEY = 'tms-mrn-label';

interface CachedMrnLabel {
  /** The authenticated hospital this term belongs to. */
  hospitalId: string;
  /** The raw configured term, NOT the resolved one — see {@link persist}. */
  label: string;
}

/**
 * Push the term into i18next's interpolation defaults.
 *
 * The `defaultVariables` object is replaced rather than mutated in place so we
 * never depend on i18next holding a particular inner reference.
 */
function applyToI18next(label: string): void {
  const options = i18next.options;
  if (!options) return;

  if (!options.interpolation) options.interpolation = {};
  options.interpolation.defaultVariables = {
    ...options.interpolation.defaultVariables,
    [MRN_LABEL_VAR]: label,
  };

  // Invalidate mounted $t() bindings (i18next-vue listens on store 'added').
  i18next.store?.emit?.('added');
}

/**
 * Remember the term for the next page load.
 *
 * Stores the *raw* configured term rather than the resolved one, so that a
 * tenant who configured nothing still picks up the correct per-language default
 * after switching language — persisting the resolved "MRN" would pin the
 * English term onto a French session.
 *
 * A no-op until the hospital is known, which deliberately excludes the login
 * page's provisional term: that value comes from a hostname lookup, so it is
 * exactly the one that must not outlive the session that fetched it.
 */
function persist(rawLabel: string): void {
  const hospitalId = config.hospitalId;
  if (!hospitalId) return;

  try {
    const entry: CachedMrnLabel = { hospitalId, label: rawLabel };
    localStorage.setItem(MRN_LABEL_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage full, disabled, or unavailable — non-fatal, the term is
    // still live for this session and re-published on the next login.
  }
}

/**
 * Apply the persisted term at app boot, before anything is mounted.
 *
 * Called from `main.ts` after `setupI18n()`, so the bundle is available for
 * {@link resolveBundleDefault}, and after `useAuthStore().initRefreshTimer()`,
 * which restores `config.hospitalId` from the persisted auth store. That
 * ordering is load-bearing: the hospital would otherwise be unknown here and
 * every reload of an authenticated session would start on "MRN".
 *
 * A no-op when unauthenticated, or when the cached term belongs to a different
 * hospital, which leaves the {@link MRN_LABEL_FALLBACK} initial value in place
 * until a publish supplies the real term.
 */
export function hydrateMrnLabel(): void {
  const hospitalId = config.hospitalId;
  if (!hospitalId) return;

  try {
    const raw = localStorage.getItem(MRN_LABEL_CACHE_KEY);
    if (!raw) return;

    const entry = JSON.parse(raw) as CachedMrnLabel;
    // Another hospital's term, or an older build's hostname-keyed entry —
    // ignore rather than mislabel.
    if (entry?.hospitalId !== hospitalId) return;

    setMrnLabel(entry.label);
  } catch {
    // Corrupt or unreadable cache — fall through to the default term.
  }
}

/**
 * Set the tenant's MRN term.
 *
 * Blank / null / whitespace-only values reset to the current language's default
 * term, so a tenant that has not configured a term — or one that clears it —
 * keeps reading "MRN" ("NDA" in French) rather than rendering an empty label.
 *
 * i18next is always initialised by the time this runs: `main.ts` awaits
 * `setupI18n()` before `app.mount()`, and every caller runs after that —
 * {@link hydrateMrnLabel} at boot, `useOidc` on the login page,
 * `useTenantSupportEmail` once the hospital is known, and `HospitalConfigView`
 * on save.
 */
export function setMrnLabel(label: string | null | undefined): void {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  const next = trimmed || resolveBundleDefault();

  _mrnLabel.value = next;
  applyToI18next(next);
  persist(trimmed);
}

/**
 * Term to use when the tenant has configured none.
 *
 * Read from the bundle rather than hardcoded, because the natural term differs
 * by language: French sites say "NDA", German sites "MRN". Without this, every
 * non-English tenant that has not set a term would regress to the English
 * "MRN" the moment their MRN strings became templated.
 *
 * `common.mrnLabelDefault` is deliberately NOT templated with `{{mrnLabel}}`,
 * so reading it here cannot recurse.
 */
function resolveBundleDefault(): string {
  const fromBundle = i18next.isInitialized
    ? i18next.t(MRN_LABEL_DEFAULT_KEY, { defaultValue: '' })
    : '';
  return typeof fromBundle === 'string' && fromBundle.trim()
    ? fromBundle.trim()
    : MRN_LABEL_FALLBACK;
}

export function useMrnLabel() {
  return {
    /** The tenant's term for the medical record number, e.g. "MRN" or "EKB". */
    mrnLabel: readonly(_mrnLabel),
  };
}
